import { Link } from "react-router-dom";
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from "../contact";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import type { Me } from "../types";
import { LogoMark } from "../Logo";
import { Icon } from "../components/Icon";

function Shot({
  src,
  alt,
  caption,
}: {
  src: string;
  alt: string;
  caption?: ReactNode;
}) {
  return (
    <figure className="shot">
      <img src={src} alt={alt} loading="lazy" />
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}

function Feature({
  eyebrow,
  title,
  children,
  shot,
}: {
  eyebrow: string;
  title: string;
  children: ReactNode;
  shot: ReactNode;
}) {
  return (
    <section className="mk-feature">
      <div className="mk-copy">
        <span className="mk-eyebrow">{eyebrow}</span>
        <h2>{title}</h2>
        {children}
      </div>
      {shot}
    </section>
  );
}

export function About() {
  // Read the shared ["me"] cache. retryOnMount:false means that if the query
  // already errored (logged out, hit via ProtectedLayout's fallback) mounting
  // this component won't kick off another /me request; it just reads whatever
  // is cached. A direct visit while logged in still does the one initial fetch.
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<Me>("/me"),
    retry: false,
    retryOnMount: false,
    staleTime: 60_000,
  });
  const loggedIn = me.isSuccess;

  return (
    <>
      <header className="site-header">
        <div className="inner">
          <Link to="/" className="brand">
            <span className="mark">
              <LogoMark size={18} />
            </span>
            <span>Caldwell School Lunch</span>
          </Link>
          {loggedIn ? (
            <Link className="btn sm" to="/" style={{ marginLeft: "auto" }}>
              <Icon name="arrow-right" size={15} />
              Go to app
            </Link>
          ) : (
            <a
              className="btn sm"
              href="/auth/google"
              style={{ marginLeft: "auto" }}
            >
              <Icon name="user" size={15} />
              Sign in
            </a>
          )}
        </div>
      </header>

      <main className="app-main marketing">
        <section className="mk-hero">
          <h1>Your school's lunch order, minus the busywork.</h1>
          <p>
            Order lunch for every kid on one screen, then get each lunch day on
            your Google&nbsp;Calendar named with the actual dish.
          </p>
          <div className="mk-cta">
            {loggedIn ? (
              <Link className="btn" to="/">
                <Icon name="arrow-right" size={16} />
                Go to dashboard
              </Link>
            ) : (
              <a className="btn" href="/auth/google">
                <Icon name="user" size={16} />
                Continue with Google
              </a>
            )}
            <a className="btn ghost" href="#how">
              See how it works
            </a>
          </div>
        </section>

        <ol className="mk-steps">
          <li>
            <span className="mk-step-ico">
              <Icon name="utensils" size={20} />
            </span>
            <strong>Pick in one pass</strong>
            <span>Hot, Swap, or Veggie for every kid. No PDF menu to cross-reference.</span>
          </li>
          <li>
            <span className="mk-step-ico">
              <Icon name="external" size={20} />
            </span>
            <strong>Submit prefilled</strong>
            <span>Open the school's real Google&nbsp;Form with every answer already filled in.</span>
          </li>
          <li>
            <span className="mk-step-ico">
              <Icon name="calendar" size={20} />
            </span>
            <strong>Auto-synced</strong>
            <span>Forward the receipt and every lunch day lands on your calendar.</span>
          </li>
        </ol>

        <p className="mk-note">
          Works alongside the school's real Google&nbsp;Form, it doesn't replace
          it. Use as much or as little as you like.
        </p>

        <div id="how" className="mk-features">
          <Feature
            eyebrow="Calendar sync"
            title="Every lunch day, on your Google Calendar."
            shot={
              <Shot
                src="/screenshots/calendar.png"
                alt="A week in Google Calendar with a lunch event each school day, titled with the dish."
                caption="One Google Calendar event per lunch day, on the calendar you choose, titled with the real dish."
              />
            }
          >
            <p>
              After you submit the school's Google&nbsp;Form, forward the
              &ldquo;Thanks for filling out this form&rdquo; receipt to your app
              address. The app reads which days and choices you picked and creates
              one Google&nbsp;Calendar event per lunch day, at the time you set,
              named with the actual dish from that day's menu.
            </p>
            <ul>
              <li>Edit your order, re-forward, and it updates in place with no duplicates.</li>
              <li>Each child can sync to their own calendar.</li>
              <li>It only ever touches the events it created, never anything else on your calendar.</li>
            </ul>
          </Feature>

          <Feature
            eyebrow="Order picker"
            title="Pick for every kid in one pass."
            shot={
              <Shot
                src="/screenshots/order-picker.png"
                alt="The order page: each school day shows its dishes with a Hot/Swap/Veggie control per child."
                caption="Each day shows the real dishes with a control for every child, plus an “Everyone” quick-set."
              />
            }
          >
            <p>
              Go through the month once. Each day shows its Hot, Swap, and Veggie
              dishes with a picker for every kid, so you can set them all at once
              or one at a time. When you're done, each child gets a link to the
              school's Google&nbsp;Form with every answer already filled in. You
              check it over and hit Submit; the app never submits for you.
            </p>
            <ul>
              <li>No more cross-referencing the PDF menu.</li>
              <li>Pizza days, the extra-slice add-on, milk, name, and grade are all filled in.</li>
              <li>After you submit, forward the receipt like normal and the calendar fills in.</li>
              <li>A child's row locks once their receipt comes back, so you never double-order.</li>
            </ul>
          </Feature>

          <Feature
            eyebrow="Menu"
            title="The month's menu, always current."
            shot={
              <Shot
                src="/screenshots/menu.png"
                alt="The monthly menu shown as a clean weekly grid."
                caption="Pulled straight from the district's live menu feed."
              />
            }
          >
            <p>
              The full month as a clean grid, with Hot, Swap, Veggie, and sides
              for every day, so you're not digging through a PDF to decide.
            </p>
          </Feature>

          <Feature
            eyebrow="Families"
            title="Built for more than one kid."
            shot={
              <Shot
                src="/screenshots/dashboard.png"
                alt="The dashboard listing each child's orders and sync status."
                caption="Every child's orders and calendar status in one place."
              />
            }
          >
            <p>
              Per-child calendars, per-child milk, and an at-a-glance &ldquo;days
              left to order&rdquo; for each kid. Forward one receipt or a stack of
              them, and the app matches each to the right child.
            </p>
          </Feature>
        </div>

        <section className="mk-trust">
          <h2>About your data</h2>
          <p>
            Sign-in is Google&nbsp;OAuth. The app can only create and update the
            calendar events it makes. It never reads or changes your other
            events. Google refresh tokens are encrypted at rest, and the app reads
            only the order emails you choose to forward.
          </p>
          <p>
            <Link to="/privacy">Privacy Policy</Link>
            {"  ·  "}
            <Link to="/terms">Terms of Service</Link>
          </p>
        </section>

        <section className="mk-start">
          <h2>Getting started</h2>
          <ol>
            <li>Sign in with Google.</li>
            <li>Add your kids and pick a Google&nbsp;Calendar for each.</li>
            <li>
              Order the month with the picker (or fill the school's form yourself),
              submit each child's form, and forward the receipts.
            </li>
          </ol>
          {loggedIn ? (
            <Link className="btn" to="/">
              <Icon name="arrow-right" size={16} />
              Go to dashboard
            </Link>
          ) : (
            <a className="btn" href="/auth/google">
              <Icon name="user" size={16} />
              Continue with Google
            </a>
          )}
        </section>

        <footer className="mk-footer">
          <p>
            Caldwell School Lunch is an independent tool built by a parent. It is
            not affiliated with or endorsed by the Caldwell-West Caldwell School
            District or Google. Questions or problems? Email{" "}
            <a href={SUPPORT_MAILTO}>{SUPPORT_EMAIL}</a>.
          </p>
        </footer>
      </main>
    </>
  );
}
